/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable func-names */
import i18n from "@i18n";
import { addMethod, string } from "yup";

// control dni letters
const dniLetters = "TRWAGMYFPDXBNJZSQVHLCKE";

const dniRegex = /^(\d{8})([A-Z])$/;
const nieRegex = /^[XYZ]\d{7,8}[A-Z]$/;

const validateDNI = (dni: string | undefined): boolean => {
    if (!dni) return false;

    const num = parseInt(dni.slice(0, dni.length - 1), 10);
    const letter = dni.charAt(dni.length - 1);
    const expectedLetter = dniLetters.charAt(num % 23);

    return expectedLetter === letter.toUpperCase() && dniRegex.test(dni);
};

const validateNIE = (nie: string | undefined): boolean => {
    if (!nie) return false;

    if (nie.startsWith("X")) {
        return validateDNI(`0${nie.slice(1)}`) && nieRegex.test(nie);
    } else if (nie.startsWith("Y")) {
        return validateDNI(`1${nie.slice(1)}`) && nieRegex.test(nie);
    } else if (nie.startsWith("Z")) {
        return validateDNI(`2${nie.slice(1)}`) && nieRegex.test(nie);
    }

    return false;
};

const validateNIF = (nif: string | undefined): boolean => {
    if (!nif) return false;
    const up = nif.toUpperCase().trim();

    // Accept either DNI (natural persons) or CIF (legal entities)
    return validateDNI(up) || validateCIF(up);
};

const validateEmail = (email: string | undefined): boolean => {
    if (!email) return false;

    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

    return emailRegex.test(email);
};

const validatePassport = (passport: string | undefined): boolean => {
    if (!passport) return false;

    const passportRegex = /^[a-z]{3}[0-9]{6}[a-z]?$/i;

    return passportRegex.test(passport);
};

// CIF (Spanish legal entities NIF) validation
// Format: 1 letter (ABCDEFGHJNPQRSUVW) + 7 digits + control (digit or letter)
// Control calculation:
//  - Sum of even-position digits (2,4,6) directly
//  - Sum of odd-position digits (1,3,5,7) after doubling each and adding its digits
//  - Control digit = (10 - (sum % 10)) % 10
//  - Control letter = 'JABCDEFGHI'[control digit]
//  - Depending on first letter: must be letter (PQRSNW), must be digit (ABEH), otherwise either is valid
const validateCIF = (cif: string | undefined): boolean => {
    if (!cif) return false;
    const up = cif.toUpperCase().trim();
    const cifRegex = /^[ABCDEFGHJNPQRSUVW]\d{7}[0-9A-J]$/i;
    if (!cifRegex.test(up)) return false;

    const first = up.charAt(0);
    const digits = up.slice(1, 8); // seven digits
    const control = up.charAt(8);

    let sum = 0;
    for (let i = 0; i < digits.length; i += 1) {
        let n = parseInt(digits[i], 10);
        // positions i=0,2,4,6 are odd positions (1,3,5,7)
        if (i % 2 === 0) {
            n *= 2;
            sum += Math.floor(n / 10) + (n % 10);
        } else {
            sum += n;
        }
    }

    const controlDigit = (10 - (sum % 10)) % 10;
    const controlLetter = "JABCDEFGHI"[controlDigit];

    const mustBeLetter = "PQRSNW".includes(first);
    const mustBeDigit = "ABEH".includes(first);

    if (mustBeLetter) return control === controlLetter;
    if (mustBeDigit) return control === String(controlDigit);
    return control === controlLetter || control === String(controlDigit);
};

addMethod(string, "dni", function () {
    return this.test(
        "dni",
        i18n.t("validation:string.dni"),
        (value) =>
            value === undefined || value.length === 0 || validateDNI(value),
    );
});

addMethod(string, "dniNie", function () {
    return this.test(
        "dniNie",
        i18n.t("validation:string.dni-nie"),
        (value) =>
            value === undefined ||
            value.length === 0 ||
            validateDNI(value) ||
            validateNIE(value),
    );
});

addMethod(string, "nie", function () {
    return this.test(
        "nie",
        i18n.t("validation:string.nie"),
        (value) =>
            value === undefined || value.length === 0 || validateNIE(value),
    );
});

addMethod(string, "nif", function () {
    return this.test(
        "nif",
        i18n.t("validation:string.nif"),
        (value) =>
            value === undefined || value.length === 0 || validateNIF(value),
    );
});

addMethod(string, "nifNie", function () {
    return this.test(
        "nifNie",
        i18n.t("validation:string.nif-nie"),
        (value) =>
            value === undefined ||
            value.length === 0 ||
            validateNIF(value) ||
            validateNIE(value),
    );
});

addMethod(string, "email", function () {
    return this.test(
        "email",
        i18n.t("validation:string.email"),
        (value) =>
            value === undefined || value.length === 0 || validateEmail(value),
    );
});

addMethod(string, "alphanumeric", function () {
    return this.test(
        "alphanumeric",
        i18n.t("validation:string.alphanumeric"),
        (value) =>
            value === undefined ||
            value.length === 0 ||
            /[a-zA-Z0-9]/.test(value),
    );
});

addMethod(string, "parseNumeric", function () {
    return this.test(
        "numeric",
        i18n.t("validation:number.integer"),
        (value) =>
            value === undefined || value.length === 0 || /^\d+$/.test(value),
    );
});

addMethod(string, "passport", function () {
    return this.test(
        "passport",
        i18n.t("validation:string.passport"),
        (value) =>
            value === undefined ||
            value.length === 0 ||
            validatePassport(value),
    );
});

const validatePhone = (phone: string | undefined): boolean => {
    if (!phone) return false;

    const phoneRegex = /^\+?[0-9]{9,20}$/;

    return phoneRegex.test(phone);
};

addMethod(string, "phone", function () {
    return this.test(
        "phone",
        i18n.t("validation:string.phone"),
        (value) =>
            value === undefined || value.length === 0 || validatePhone(value),
    );
});

addMethod(string, "parseWithDecimals", function () {
    return this.test(
        "numericWithDecimals",
        i18n.t("validation:number.integerWithDecimals"),
        (value) =>
            value === undefined ||
            value === null ||
            value.length === 0 ||
            /^\d+(\.\d{1,2})?$/.test(value.trim()),
    );
});

addMethod(string, "alphabeticOnly", function () {
    return this.test(
        "alphabeticOnly",
        i18n.t("validation:string.alphabeticOnly"),
        (value) =>
            value === undefined ||
            value.length === 0 ||
            /^[a-zA-ZáéíóúÁÉÍÓÚüÜñÑ\s]+$/.test(value),
    );
});
